import _ from 'lodash';
import { bytes } from 'Utils/filters';
import template from './dataUsage.html';

export default {
  name: 'adminDataUsageComponent',
  template,
  controller: DataUsageController,
  controllerAs: 'DataUsageCtrl'
};

/* @ngInject */
function DataUsageController(
  $timeout,
  $q,
  $log,
  uiGridConstants,
  DataUsageService,
  RcuInfoService,
  EquipmentService
) {

  // /////////////// //
  // Local Variables //
  // /////////////// //
  const vm = this;
  const defaultGridSettings = {
    enableColumnMenus: false,
    enableFiltering: true,
    showGridFooter: true,
    showColumnFooter: true,
    enableRowSelection: true,
    enableSelectAll: true,
    multiSelect: true,
    modifierKeysToMultiSelect: true,
    gridMenuShowHideColumns: false,
    enableRowHeaderSelection: false,
    enableColumnResizing: true
  };
  const rcuMappings = {};
  const equipmentMapping = {};
  const rcuFilter = {};

  const timeoutMilli = 700;
  const divHalf = 2;

  /// /////////////////
  // Public Variables//
  /// /////////////////
  vm.serverGrid = _.defaults({
    enableRowSelection: false,
    columnDefs: [{
      field: '_id',
      displayName: 'Day',
      type: 'date',
      cellFilter: 'date:\'MMMM d, y\' : \'UTC\'',
      enableCellEdit: false,
      sort: {
        direction: uiGridConstants.DESC
      }
    }, {
      field: 'prettyUsage',
      displayName: 'Usage',
      type: 'string',
      enableCellEdit: false
    }]
  }, defaultGridSettings);

  vm.rcuGrid = _.defaults({
    columnDefs: [{
      field: 'name',
      displayName: 'Name',
      type: 'string',
      enableCellEdit: false,
      // Natural sort (to handle eg "Thing 10" > "Thing 2")
      sortingAlgorithm: (a, b) => a.localeCompare(b, undefined, { numeric: true }),

    }, {
      field: 'id',
      displayName: 'id',
      type: 'string',
      enableCellEdit: false,
      sort: {
        direction: uiGridConstants.ASC
      }
    }, {
      field: 'ip',
      displayName: 'ip',
      enableCellEdit: false,
      type: 'string'
    }, {
      field: 'port',
      displayName: 'Port',
      enableCellEdit: false,
      type: 'number'
    }, {
      field: 'version',
      displayName: 'Version',
      type: 'string',
      enableCellEdit: false
    }, {
      field: 'usage',
      type: 'number',
      visible: false
    }, {
      field: 'prettyUsage',
      displayName: 'Data Usage (today)',
      type: 'string',
      enableCellEdit: false,
      sortingAlgorithm: sortByBytes
    }],
    onRegisterApi(gridApi) {
      vm.rcuGrid = gridApi;
      $timeout(() => {
        // this call fixes the issue with the grid rendering smaller on page load
        vm.rcuGrid.core.handleWindowResize();
      }, timeoutMilli);
      // event handler for when a single row is selected
      gridApi.selection.on.rowSelectionChanged(null, setRcuIdFilter);
      // event handler for when multiple rows are selected
      gridApi.selection.on.rowSelectionChangedBatch(null, setRcuIdFilter);
    }
  }, defaultGridSettings);

  vm.equipmentGrid = _.defaults({
    enableRowSelection: false,
    columnDefs: [{
      field: 'name',
      displayName: 'Name',
      type: 'string',
      enableCellEdit: false,
      sort: { direction: uiGridConstants.ASC },
      // Natural sort (to handle eg "Thing 10" > "Thing 2")
      sortingAlgorithm: (a, b) => a.localeCompare(b, undefined, { numeric: true }),

    }, {
      field: 'id',
      displayName: 'id',
      type: 'string',
      enableCellEdit: false
    }, {
      field: 'rcu',
      displayName: 'Rcu Id',
      type: 'string',
      enableCellEdit: false,
      filter: {
        condition: multiFilter
      }
    }, {
      field: 'settings.externalIp',
      displayName: 'External Address',
      enableCellEdit: false,
      type: 'string'
    }, {
      field: 'settings.externalPort',
      displayName: 'External Port',
      enableCellEdit: false,
      type: 'number'
    }, {
      field: 'usage',
      type: 'number',
      visible: false
    }, {
      field: 'prettyUsage',
      displayName: 'Data Usage (today)',
      type: 'string',
      enableCellEdit: false,
      sortingAlgorithm: sortByBytes
    }],
    onRegisterApi(gridApi) {
      vm.equipmentGrid = gridApi;
      $timeout(() => {
        // this call fixes the issue with the grid rendering smaller on page load
        vm.equipmentGrid.core.handleWindowResize();
      }, timeoutMilli);
      // event handler for when a single row is selected
      gridApi.selection.on.rowSelectionChanged(null, () => { /**/ });
      // event handler for when multiple rows are selected
      gridApi.selection.on.rowSelectionChangedBatch(null, () => { /**/ });
    }
  }, defaultGridSettings);


  vm.$onInit = init;

  function init() {
    return $q.all([
      getDailyUsage(),
      getRcuUsage(),
      getEquipmentUsage()
    ])
      .catch((err) => {
        $log.error('Could not load some usage information:', err.message);
      });
  }

  function getDailyUsage() {
    return DataUsageService.getDailyUsage()
      .then((usageByDay) => {
        usageByDay.forEach((usage) => {
          usage.prettyUsage = bytes(usage.usage, 2);
        });
        vm.serverGrid.data = usageByDay;
      });
  }

  function getRcuUsage() {
    return $q.all([
      RcuInfoService.getAllRcus(),
      DataUsageService.getRcuUsage()
    ])
      .then((resps) => {
        const rcus = resps[0];
        rcus.forEach((rcu) => {
          rcuMappings[rcu.id] = rcu;
          rcuMappings[rcu.id].version = rcu.version ? rcu.version.dnversion : 'unknown';
        });
        vm.rcuGrid.data = rcus;

        const usages = resps[1];
        usages.forEach((rcuUsage) => {
          if (rcuMappings[rcuUsage._id]) {
            rcuMappings[rcuUsage._id].usage = rcuUsage.usage;
            rcuMappings[rcuUsage._id].prettyUsage = bytes(rcuUsage.usage, divHalf);
          }
        });
      });
  }

  function getEquipmentUsage() {
    return $q.all([
      EquipmentService.getEquipmentList(),
      DataUsageService.getEquipmentUsage()
    ])
      .then((resps) => {
        const equipment = resps[0].body;
        _.forEach(equipment, (equip) => {
          equipmentMapping[equip.id] = equip;
        });
        vm.equipmentGrid.data = equipment;

        const usages = resps[1];
        _.forEach(usages, (equipmentUsage) => {
          if (equipmentMapping[equipmentUsage._id]) {
            equipmentMapping[equipmentUsage._id].usage = equipmentUsage.usage;
            equipmentMapping[equipmentUsage._id].prettyUsage = bytes(equipmentUsage.usage, divHalf);
          }
        });
      });
  }

  // Sort by usage instead of sorting by the prettyUsage which is just a
  // string Another version of this would be to store a key/value object with
  // prettyUsage-usage and just perform key value look ups instead of
  // defining this sorting algorithm with an invisible column.
  // TODO: Remove unused parameters and refactor usages
  function sortByBytes(__a, __b, rowA, rowB) {
    const usageA = rowA.entity.usage;
    const usageB = rowB.entity.usage;
    if (usageA && usageB) {
      return rowA.entity.usage - rowB.entity.usage;
    } else if (!usageA && !usageB) {
      return 0;
    } else if (usageA) {
      return 1;
    }
    return -1;

  }

  function setRcuIdFilter(rows) {
    if (!_.isArray(rows)) {
      rows = [rows];
    }

    _.forEach(rows, (row) => {
      if (row.isSelected) {
        rcuFilter[row.entity.id] = true;
      } else {
        delete rcuFilter[row.entity.id];
      }
    });
    vm.equipmentGrid.grid.columns[2].filters[0].term = _.keys(rcuFilter)
      .join(':');
  }

  // See http://stackoverflow.com/questions/30085872/how-to-filter-with-multiple-values-in-ui-grid-angularjs
  function multiFilter(searchTerm, cellValue) {
    const separators = ['-', '/', ':', ';', ','];
    const strippedValue = searchTerm.split(new RegExp(separators.join('|'), 'g'));
    const loweredCellVal = cellValue.toLowerCase();
    return _.some(strippedValue, (val) => {
      val = val.replace(' ', '');
      if (_.includes(loweredCellVal, val.toLowerCase())) {
        return true;
      }
      return false;
    });
  }
}
